import React, { useState, useEffect } from "react";
import { Grid, Card, Typography, Box } from "@mui/material";
import { styled } from "@mui/system";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import TraditionalSearch from "./TraditionalSearch";
import VectorSearch from "./VectorSearch";
import { generateColumns } from "../../helpers/generateColumns";
import DataTable from "./DataTable";
import StoreSelector from "components/StoreSelector";
import MDSnackbar from "components/MDSnackbar";
import { useAuth0 } from "@auth0/auth0-react";

const SearchResultsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const TraditionalSearchResultsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const SearchDashboard = () => {
  const { user } = useAuth0();

  const [searchQuery, setSearchQuery] = useState("");
  const [traditionalSearchValues, setTraditionalSearchValues] = useState({});
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [vectorSearchResults, setVectorSearchResults] = useState(null);
  const [traditionalSearchResults, setTraditionalSearchResults] = useState(null);
  const [isVectorSearchLoading, setIsVectorSearchLoading] = useState(false);
  const [isTraditionalSearchLoading, setIsTraditionalSearchLoading] = useState(false);
  const [traditionalSearchError, setTraditionalSearchError] = useState(false);
  const [description, setDescription] = useState("");
  const [error, setError] = useState(false);
  const [selectedStore, setSelectedStore] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const stores = [
    { id: "store1", name: "Store 1" },
    { id: "store2", name: "Store 2" },
    // Add more stores as needed
  ];

  useEffect(() => {
    if (selectedStore) {
      fetchColumns();
    }
  }, [selectedStore]);

  const fetchColumns = async () => {
    try {
      const response = await fetch(
        `${baseUrl}/columns?storeId=${selectedStore}&userId=${user.sub}`
      );
      if (!response.ok) {
        throw new Error("Something went wrong");
      }
      const fetchedColumns = await response.json();
      setColumns(fetchedColumns);
    } catch (error) {
      console.error(error);
    }
  };

  const baseUrl = "https://veclogic.com/backend";

  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
  };

  const handleVectorSearch = async () => {
    if (!selectedStore) {
      setShowNotification(true);
      return;
    }
    setIsVectorSearchLoading(true);
    setError(false);

    try {
      const response = await fetch(`${baseUrl}/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: searchQuery, storeId: selectedStore, authId: user.sub }),
      });
      if (!response.ok) {
        throw new Error("Something went wrong");
      }
      let fetchedData = await response.json();
      if (fetchedData.description) {
        setDescription(fetchedData.description);
      } else {
        setDescription("");
      }
      fetchedData.results = fetchedData.results.map((row, index) => {
        return { id: index, ...row };
      });
      setData(fetchedData);
      setVectorSearchResults(fetchedData);
      setIsVectorSearchLoading(false);
    } catch (error) {
      setIsVectorSearchLoading(false);
      setError(true);
      console.error(error);
    }
  };

  const handleTraditionalSearch = async () => {
    if (!selectedStore) {
      setShowNotification(true);
      return;
    }
    setIsTraditionalSearchLoading(true);
    setTraditionalSearchError(false);

    try {
      const criteriaObject = criteria.reduce((obj, item) => {
        obj[item.column] = item.value;
        return obj;
      }, {});

      const response = await fetch(`${baseUrl}/mongo-search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          criteria: criteriaObject,
          storeId: selectedStore,
          userId: userId,
        }),
      });

      if (!response.ok) {
        throw new Error("Something went wrong");
      }

      const fetchedData = await response.json();
      fetchedData.results = fetchedData.results.map((row, index) => {
        const { _id, ...rest } = row;
        return {
          id: index,
          ...rest,
        };
      });

      setTraditionalSearchResults(fetchedData);
      setIsTraditionalSearchLoading(false);
    } catch (error) {
      setIsTraditionalSearchLoading(false);
      setTraditionalSearchError(true);
      console.error(error);
    }
  };

  const updateCriteria = (criteria) => {
    setTraditionalSearchValues(criteria);
  };

  const handleCloseNotification = () => setShowNotification(false);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Typography variant="h5" gutterBottom>
                  Search
                </Typography>
                <Grid container spacing={4} alignItems="stretch">
                  <Grid item xs={12}>
                    <StoreSelector
                      stores={stores}
                      selectedStore={selectedStore}
                      onStoreChange={handleStoreChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SearchContainer>
                      <VectorSearch
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        handleSearch={handleVectorSearch}
                        isLoading={isVectorSearchLoading}
                        error={error}
                        setError={setError}
                        description={description}
                      />
                      <SearchResultsContainer>
                        {vectorSearchResults && vectorSearchResults.results.length > 0 && (
                          <DataTable
                            data={vectorSearchResults}
                            columns={generateColumns(vectorSearchResults.results)}
                            title="Vector Search Results"
                          />
                        )}
                      </SearchResultsContainer>
                    </SearchContainer>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SearchContainer>
                      <TraditionalSearch
                        handleTraditionalSearch={handleTraditionalSearch}
                        columns={columns}
                        setColumns={setColumns}
                        updateCriteria={updateCriteria}
                        isLoading={isTraditionalSearchLoading}
                        error={traditionalSearchError}
                        setError={setTraditionalSearchError}
                        selectedStore={selectedStore}
                        userId={user.sub}
                      />
                      <TraditionalSearchResultsContainer>
                        {traditionalSearchResults &&
                          traditionalSearchResults.results.length > 0 && (
                            <DataTable
                              data={traditionalSearchResults}
                              columns={generateColumns(traditionalSearchResults.results)}
                              title="Traditional Search Results"
                            />
                          )}
                      </TraditionalSearchResultsContainer>
                    </SearchContainer>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <MDSnackbar
        color="warning"
        icon="warning"
        title="No Store Selected"
        content="Please select a store before performing a search."
        open={showNotification}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </DashboardLayout>
  );
};

export default SearchDashboard;
